import type { ComponentType } from 'react';
import {
  AlignLeft,
  AppWindow,
  Armchair,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  BadgeCheck,
  Bed,
  BedDouble,
  Bike,
  Bold,
  BookOpen,
  Bookmark,
  BookmarkPlus,
  Bot,
  Briefcase,
  Bug,
  Building,
  Building2,
  Bus,
  Calendar,
  CalendarClock,
  CalendarDays,
  Car,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Church,
  Circle,
  CircleAlert,
  CircleCheck,
  CircleDot,
  CircleDotDashed,
  CircleHelp,
  CircleMinus,
  CircleUser,
  CircleX,
  Clock,
  Cloud,
  CloudOff,
  Compass,
  CreditCard,
  Cross,
  Download,
  Ellipsis,
  ExternalLink,
  File,
  FileDown,
  FileText,
  Filter,
  Fingerprint,
  Folder,
  Footprints,
  Globe,
  Heading,
  Headset,
  Heart,
  History,
  Hourglass,
  House,
  Image,
  Images,
  IndentDecrease,
  IndentIncrease,
  Info,
  Italic,
  Key,
  Landmark,
  Layers,
  LifeBuoy,
  Lightbulb,
  Link,
  List,
  ListChecks,
  Lock,
  LogIn,
  LogOut,
  Luggage,
  Mail,
  Map,
  MapPin,
  Martini,
  Megaphone,
  Menu,
  MessageCircle,
  MessageSquare,
  Minus,
  Moon,
  Navigation,
  Pencil,
  Phone,
  Plane,
  PlaneLanding,
  PlaneTakeoff,
  Play,
  Plus,
  Printer,
  Receipt,
  Search,
  SearchX,
  Send,
  Shapes,
  Share,
  Ship,
  Smartphone,
  Snowflake,
  Sparkles,
  Square,
  SquareCheck,
  Star,
  StickyNote,
  Sun,
  Tag,
  Text,
  Ticket,
  TrafficCone,
  TrainFront,
  Trash2,
  Trees,
  TriangleAlert,
  Umbrella,
  Unlink,
  Upload,
  User,
  UserPlus,
  Users,
  Utensils,
  Wallet,
  WandSparkles,
  X,
  Zap,
} from 'lucide-react-native';

/**
 * Icon registry — the single source of truth for which icons the app ships
 * and which library backs them. Every icon is referenced by a stable
 * semantic name (kept from the prior MaterialIcons set so call sites didn't
 * churn). To swap icon libraries, change only the imports + values here;
 * `IconName` and all ~250 call sites stay put. Only the lucide components
 * imported below are bundled (tree-shaken) — no font, no glyph map.
 */
type LucideIcon = ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;

export const ICON_REGISTRY = {
  'ac-unit': Snowflake,
  'access-time': Clock,
  'account-circle': CircleUser,
  'add': Plus,
  'airline-stops': Plane,
  'android': Smartphone,
  'apartment': Building2,
  'arrow-back': ArrowLeft,
  'arrow-downward': ArrowDown,
  'arrow-forward': ArrowRight,
  'arrow-upward': ArrowUp,
  'auto-awesome': Sparkles,
  'auto-fix-high': WandSparkles,
  'badge': BadgeCheck,
  'bed': Bed,
  'bedtime': Moon,
  'blur-on': CircleDotDashed,
  'bookmark': Bookmark,
  'bookmark-add': BookmarkPlus,
  'bookmark-outline': Bookmark,
  'bug-report': Bug,
  'business': Building,
  'calendar-today': Calendar,
  'call': Phone,
  'campaign': Megaphone,
  'cancel': CircleX,
  'car-rental': Car,
  'card-travel': Briefcase,
  'category': Shapes,
  'chat': MessageCircle,
  'chat-bubble-outline': MessageCircle,
  'check': Check,
  'check-box': SquareCheck,
  'check-box-outline-blank': Square,
  'check-circle': CircleCheck,
  'check-circle-outline': CircleCheck,
  'checklist': ListChecks,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'church': Church,
  'clear': X,
  'close': X,
  'cloud': Cloud,
  'cloud-off': CloudOff,
  'cloud-queue': Cloud,
  'confirmation-number': Ticket,
  'credit-card': CreditCard,
  'delete': Trash2,
  'delete-outline': Trash2,
  'description': FileText,
  'directions': Navigation,
  'directions-bike': Bike,
  'directions-boat': Ship,
  'directions-bus': Bus,
  'directions-car': Car,
  'directions-walk': Footprints,
  'done': Check,
  'download': Download,
  'edit': Pencil,
  'edit-location': MapPin,
  'edit-note': Pencil,
  'email': Mail,
  'error': CircleAlert,
  'error-outline': CircleAlert,
  'event': Calendar,
  'event-note': CalendarDays,
  'event-seat': Armchair,
  'expand-less': ChevronUp,
  'expand-more': ChevronDown,
  'explore': Compass,
  'favorite': Heart,
  'favorite-border': Heart,
  'feedback': MessageSquare,
  'file-download': FileDown,
  'filter': Filter,
  'fingerprint': Fingerprint,
  'flash-on': Zap,
  'flight': Plane,
  'flight-land': PlaneLanding,
  'flight-takeoff': PlaneTakeoff,
  'folder-shared': Folder,
  'format-bold': Bold,
  'format-indent-decrease': IndentDecrease,
  'format-indent-increase': IndentIncrease,
  'format-italic': Italic,
  'format-list-bulleted': List,
  'group': Users,
  'group-add': UserPlus,
  'help': CircleHelp,
  'help-outline': CircleHelp,
  'history': History,
  'home': House,
  'horizontal-rule': Minus,
  'hotel': BedDouble,
  'hourglass-empty': Hourglass,
  'https': Lock,
  'image': Image,
  'info': Info,
  'info-outline': Info,
  'insert-drive-file': File,
  'ios-share': Share,
  'key': Key,
  'keyboard-arrow-down': ChevronDown,
  'keyboard-arrow-up': ChevronUp,
  'label': Tag,
  'language': Globe,
  'layers': Layers,
  'light': Lightbulb,
  'lightbulb-outline': Lightbulb,
  'link': Link,
  'link-off': Unlink,
  'list': List,
  'local-activity': Ticket,
  'local-hospital': Cross,
  'local-offer': Tag,
  'local-taxi': Car,
  'location-on': MapPin,
  'lock': Lock,
  'lock-outline': Lock,
  'login': LogIn,
  'logout': LogOut,
  'luggage': Luggage,
  'mail': Mail,
  'mail-outline': Mail,
  'map': Map,
  'menu': Menu,
  'menu-book': BookOpen,
  'mode': Pencil,
  'more-horiz': Ellipsis,
  'mosque': Landmark,
  'museum': Landmark,
  'nature': Trees,
  'nightlife': Martini,
  'notes': FileText,
  'open-in-new': ExternalLink,
  'park': Trees,
  'password': Lock,
  'payments': Wallet,
  'pending': Clock,
  'people': Users,
  'person': User,
  'person-add': UserPlus,
  'phone-android': Smartphone,
  'photo': Image,
  'photo-library': Images,
  'picture-as-pdf': FileText,
  'pin': MapPin,
  'place': MapPin,
  'print': Printer,
  'public': Globe,
  'radio-button-checked': CircleDot,
  'radio-button-unchecked': Circle,
  'receipt': Receipt,
  'receipt-long': Receipt,
  'remove-circle-outline': CircleMinus,
  'restaurant': Utensils,
  'restaurant-menu': Utensils,
  'schedule': Clock,
  'search': Search,
  'search-off': SearchX,
  'send': Send,
  'short-text': Text,
  'sim-card': CreditCard,
  'smart-toy': Bot,
  'stadium': Landmark,
  'star': Star,
  'start': Play,
  'sticky-note-2': StickyNote,
  'subject': AlignLeft,
  'subway': TrainFront,
  'support': LifeBuoy,
  'support-agent': Headset,
  'synagogue': Landmark,
  'tag': Tag,
  'title': Heading,
  'today': Calendar,
  'traffic': TrafficCone,
  'train': TrainFront,
  'umbrella': Umbrella,
  'upcoming': CalendarClock,
  'upload-file': Upload,
  'warning': TriangleAlert,
  'warning-amber': TriangleAlert,
  'wb-cloudy': Cloud,
  'wb-sunny': Sun,
  'web': Globe,
  'window': AppWindow,
} satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICON_REGISTRY;
